import { formatarCnae, formatarCnpj, montarContexto, renderizar } from '@crm/contracts';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ModalConversa, ModalEmail, type RespostaA } from './EmailCliente';
import { CORES_UI, porGrupo, StatusBadge, useStatusClientes } from '../status';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Modal, TagChip, Toast, useToast } from '../components/ui';
import { data, dataHora, diasDesde, formatarWhatsapp, hojeISO, moeda } from '../format';
import { SITUACAO_COR } from '../cnpj';
import { abrirWhatsapp } from '../whatsapp';
import { ClienteForm, VAZIO } from './ClienteForm';
import { CardNegociacoes } from './CardNegociacoes';
import { CardVendedores, type VendedorAdicional } from './CardVendedores';
import { erroNeg, estadoNegInicial, estadoPara, payloadNeg, SecaoNegociacao, valorDeTexto, type EstadoNeg } from './NegociacaoContato';
import { deOutro, useConfigNegociacao, type NegociacaoCliente } from '../negociacao';
import { useUsuarios, type Tag } from './Clientes';

interface Cliente {
  id: string; nome: string; whatsapp: string | null; empresa: string | null; documento: string | null; email: string | null;
  cidade: string | null; estado: string | null; segmento: string | null; status: string; statusId: string; statusNome: string; statusCor: string; ultimaCompra: string | null;
  valorUltimaCompra: string | null; ultimoContato: string | null; vendedorId: string | null; vendedorNome: string | null;
  vendedoresAdicionais: VendedorAdicional[];
  observacoes: string | null; baseLegal: string | null; consentimentoOrigem: string | null; consentimentoEm: string | null;
  situacaoCadastral: string | null; cnaePrincipal: string | null; situacaoVerificadaEm: string | null;
  comunicacao: 'PERMITIDO' | 'NAO_CONTACTAR'; optOutEm: string | null; optOutMotivo: string | null; tags: Tag[];
  canalPreferido: 'WHATSAPP' | 'EMAIL' | null; emailOptOutEm: string | null;
  oportunidades: Array<NegociacaoCliente & { previsaoFechamento: string | null }>;
  tarefas: Array<{ id: string; tipo: string; titulo: string; venceEm: string; responsavel: string }>;
}
interface Atividade {
  id: string; tipo: string; descricao: string; createdAt: string; usuario: string | null; campanha: string | null;
  dados: { mensagem?: string | null; emailId?: string; assunto?: string; trecho?: string; de?: string; anexos?: string[]; valor?: number; data?: string; oportunidadeFechada?: boolean };
}

const BASE: Record<string, string> = { LEGITIMO_INTERESSE: 'Legítimo interesse', CONSENTIMENTO: 'Consentimento', EXECUCAO_CONTRATO: 'Execução de contrato' };
const TITULO: Record<string, string> = {
  WHATSAPP_ENVIADO: 'WhatsApp enviado', WHATSAPP_ABERTO: 'WhatsApp aberto', VENDA_REGISTRADA: 'Venda registrada', NEGOCIACAO_ETAPA: 'Negociação', NEGOCIACAO_PERDIDA: 'Negociação perdida', OPORTUNIDADE_CRIADA: 'Oportunidade criada',
  NOTA_ADICIONADA: 'Nota', CONTATO_REGISTRADO: 'Contato registrado', CLIENTE_NAO_CONTACTAR: 'Não contactar',
  EMAIL_ENVIADO: 'E-mail enviado', EMAIL_RECEBIDO: 'Resposta por e-mail', EMAIL_DESCADASTRO: 'Descadastro de e-mail',
  WHATSAPP_CLICADO: 'Clicou no WhatsApp do e-mail',
};
const EMAIL_TIPOS = new Set(['EMAIL_ENVIADO', 'EMAIL_RECEBIDO']);

export function ClienteFichaPage() {
  const { id } = useParams<{ id: string }>();
  const { me, pode } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState(false);
  const [modal, setModal] = useState<null | 'whatsapp' | 'contato' | 'optout' | 'retorno' | 'email' | 'conversa'>(null);
  const [respostaA, setRespostaA] = useState<RespostaA | null>(null);
  const [nota, setNota] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [negInicial, setNegInicial] = useState<{ opcao: string; oportunidadeId: string } | null>(null);
  const negCfg = useConfigNegociacao();

  const q = useQuery({ queryKey: ['cliente', id], queryFn: () => api<Cliente>(`/clientes/${id}`) });
  const tl = useInfiniteQuery({
    queryKey: ['timeline', id],
    queryFn: ({ pageParam }) => api<{ data: Atividade[]; nextCursor: string | null }>(`/clientes/${id}/timeline${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  });
  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ['cliente', id] }), qc.invalidateQueries({ queryKey: ['timeline', id] })]);

  if (q.isLoading) return <Carregando />;
  if (q.error) return <><Link to="/clientes" className="small">← Clientes</Link><Erro erro={mensagemErro(q.error)} /></>;
  const c = q.data!;
  const bloqueado = c.comunicacao === 'NAO_CONTACTAR';
  const emailBloqueado = bloqueado || !!c.emailOptOutEm || !c.email;
  const preferEmail = c.canalPreferido === 'EMAIL';
  const abrirEmail = (r: RespostaA | null = null) => { setRespostaA(r); setModal('email'); };
  async function mudarCanal(canal: 'WHATSAPP' | 'EMAIL' | null) {
    try {
      await api(`/clientes/${c.id}`, { method: 'PATCH', body: { canalPreferido: canal } });
      await recarregar();
      toast.show('Canal preferido atualizado.');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  if (editando) {
    return (
      <>
        <h1>Editar cliente</h1>
        <ClienteForm
          edicao
          negociacoes={c.oportunidades}
          inicial={{
            ...VAZIO, nome: c.nome, whatsapp: c.whatsapp ?? '', empresa: c.empresa ?? '', documento: c.documento ?? '', email: c.email ?? '',
            cidade: c.cidade ?? '', estado: c.estado ?? '', segmento: c.segmento ?? '', statusId: c.statusId, ultimaCompra: c.ultimaCompra ?? '',
            valorUltimaCompra: c.valorUltimaCompra ?? '', vendedorId: c.vendedorId ?? '', vendedoresAdicionais: c.vendedoresAdicionais.map((x) => x.id), tagIds: c.tags.map((t) => t.id), observacoes: c.observacoes ?? '',
          }}
          onCancelar={() => setEditando(false)}
          onSalvar={async (payload) => {
            await api(`/clientes/${c.id}`, { method: 'PATCH', body: payload });
            await recarregar();
            await qc.invalidateQueries({ queryKey: ['clientes'] });
            setEditando(false);
            toast.show('Cliente atualizado.');
          }}
        />
      </>
    );
  }

  async function salvarNota() {
    if (!nota.trim()) return;
    try {
      await api(`/clientes/${c.id}/notas`, { body: { texto: nota } });
      setNota('');
      await recarregar();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  const dias = diasDesde(c.ultimaCompra);
  return (
    <>
      <Link to="/clientes" className="small">← Clientes</Link>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div className="logo" style={{ width: 52, height: 52, borderRadius: 26, background: '#1E293B', color: 'var(--blue-text)', fontSize: 18 }}>
          {c.nome.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 24 }}>{c.nome}</h1>
            {bloqueado ? <span className="badge red">Não contactar</span> : <span className="badge green">Comunicação permitida</span>}
            <StatusBadge nome={c.statusNome} cor={c.statusCor} />
          </div>
          <span className="muted">{[c.empresa, c.cidade && `${c.cidade}${c.estado ? `/${c.estado}` : ''}`, c.vendedorNome && `carteira de ${c.vendedorNome}${c.vendedoresAdicionais.length ? ` +${c.vendedoresAdicionais.length}` : ''}`].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="actions">
          <button className={`btn${preferEmail ? '' : ' primary'}`} disabled={bloqueado || !c.whatsapp} onClick={() => setModal('whatsapp')}><Icon name="whatsapp" size={15} />WhatsApp</button>
          <button className={`btn${preferEmail ? ' primary' : ''}`} disabled={emailBloqueado} onClick={() => abrirEmail()}
            title={!c.email ? 'Cadastre o e-mail do cliente' : c.emailOptOutEm ? 'O cliente se descadastrou dos e-mails' : undefined}>
            <Icon name="email" size={15} />E-mail
          </button>
          <button className="btn" onClick={() => { setNegInicial(null); setModal('contato'); }}>Registrar contato</button>
          <button className="btn" onClick={() => setModal('retorno')}><Icon name="agenda" size={15} />Agendar retorno</button>
          {pode('CLIENTES_EDITAR') && <button className="btn" onClick={() => setEditando(true)}>Editar</button>}
        </div>
      </div>
      <Erro erro={erro} />

      <div className="row" style={{ gap: 20 }}>
        <div className="col" style={{ flex: '1 1 300px' }}>
          <section className="card">
            <h2>Dados</h2>
            <dl className="dados">
              <dt>WhatsApp</dt><dd className="num">{formatarWhatsapp(c.whatsapp)}</dd>
              {c.email && <><dt>E-mail</dt><dd>{c.email}{c.emailOptOutEm && <> <span className="badge red">descadastrado</span></>}</dd></>}
              {c.documento && <><dt>{c.documento.length === 14 ? 'CNPJ' : 'CPF'}</dt><dd className="num">{c.documento.length === 14 ? formatarCnpj(c.documento) : c.documento}</dd></>}
              {c.situacaoCadastral && (
                <><dt>Receita</dt><dd style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={`badge ${SITUACAO_COR[c.situacaoCadastral] ?? ''}`}>{c.situacaoCadastral}</span>
                  {c.situacaoVerificadaEm && <span className="small muted">verificado em {dataHora(c.situacaoVerificadaEm)}</span>}
                </dd></>
              )}
              {c.cnaePrincipal && <><dt>CNAE</dt><dd className="num">{formatarCnae(c.cnaePrincipal)}</dd></>}
              {c.segmento && <><dt>Segmento</dt><dd>{c.segmento}</dd></>}
              <dt>Última compra</dt><dd className="num">{c.ultimaCompra ? `${data(c.ultimaCompra)}${c.valorUltimaCompra ? ` · ${moeda(c.valorUltimaCompra)}` : ''} (há ${dias} dias)` : '—'}</dd>
              <dt>Último contato</dt><dd className="num">{dataHora(c.ultimoContato)}</dd>
              {c.observacoes && <><dt>Observações</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{c.observacoes}</dd></>}
            </dl>
            {c.tags.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{c.tags.map((t) => <TagChip key={t.id} {...t} />)}</div>}
          </section>

          <CardVendedores cliente={c} podeEditar={pode('CLIENTES_ATRIBUIR')} onSalvo={recarregar} negociacoes={c.oportunidades} />

          <section className="card" aria-labelledby="h-canal">
            <h2 id="h-canal">Canal preferido</h2>
            <div className="seg" role="group" aria-label="Canal preferido do cliente">
              {([['WHATSAPP', 'WhatsApp'], ['EMAIL', 'E-mail'], [null, 'Padrão da empresa']] as const).map(([v, l]) => (
                <button key={l} type="button" aria-pressed={c.canalPreferido === v} disabled={!pode('CLIENTES_EDITAR')} onClick={() => void mudarCanal(v)}>{l}</button>
              ))}
            </div>
            <span className="small muted">
              {c.canalPreferido === 'EMAIL' ? 'Prefere e-mail: o botão E-mail fica em destaque.'
                : c.canalPreferido === 'WHATSAPP' ? 'Prefere WhatsApp.' : 'Sem preferência: segue o canal padrão da empresa.'}
              {preferEmail && !c.email ? ' Falta cadastrar o e-mail.' : ''}
            </span>
          </section>

          <section className="card">
            <h2>LGPD</h2>
            <dl className="dados">
              <dt>Base legal</dt><dd>{BASE[c.baseLegal ?? ''] ?? '—'}</dd>
              <dt>Origem do contato</dt><dd>{c.consentimentoOrigem ?? '—'}</dd>
              {bloqueado && <><dt>Opt-out</dt><dd>{dataHora(c.optOutEm)} — {c.optOutMotivo}</dd></>}
              {c.emailOptOutEm && <><dt>E-mail</dt><dd>Descadastrado em {dataHora(c.emailOptOutEm)} (WhatsApp segue liberado)</dd></>}
            </dl>
            {!bloqueado && <button className="btn sm danger block" onClick={() => setModal('optout')}>Marcar como NÃO CONTACTAR</button>}
          </section>

          {negCfg.data?.ativa && (
            <CardNegociacoes cfg={negCfg.data} negs={c.oportunidades} meuId={me?.usuario.id} veTodas={pode('RELATORIOS_TENANT')}
              onPasso={(opcao, oportunidadeId) => { setNegInicial({ opcao, oportunidadeId }); setModal('contato'); }} />
          )}
          {((!negCfg.data?.ativa && c.oportunidades.length > 0) || c.tarefas.length > 0) && (
            <section className="card">
              <h2>{negCfg.data?.ativa ? 'Retornos' : 'Oportunidades e retornos'}</h2>
              {!negCfg.data?.ativa && c.oportunidades.map((o) => (
                <div key={o.id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13.5 }}>{o.titulo}</span>
                    <span className="small faint">{o.etapa}{o.campanha ? ` · ${o.campanha}` : ''}</span>
                  </div>
                  <span className="num" style={{ marginLeft: 'auto' }}>{moeda(o.valor)}</span>
                </div>
              ))}
              {c.tarefas.map((t) => (
                <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--amber)' }} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}><span style={{ fontSize: 13.5 }}>{t.titulo}</span><span className="small faint num">{dataHora(t.venceEm)} · {t.responsavel}</span></div>
                </div>
              ))}
            </section>
          )}
        </div>

        <div className="col" style={{ flex: '2.4 1 520px' }}>
          <section className="card">
            <h2>Timeline</h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <label className="field" style={{ flex: 1 }}><span>Adicionar nota</span>
                <textarea className="textarea" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ex.: pediu para retornar depois do dia 10" />
              </label>
              <button className="btn" onClick={salvarNota} disabled={!nota.trim()}>Salvar nota</button>
            </div>
            <ol className="timeline">
              {tl.data?.pages.flatMap((p) => p.data).map((a) => (
                <li key={a.id}>
                  <time className="num" dateTime={a.createdAt}>{dataHora(a.createdAt)}</time>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 13.5 }}>
                      {TITULO[a.tipo] ? <strong style={{ fontWeight: 600 }}>{TITULO[a.tipo]}</strong> : null}
                      {TITULO[a.tipo] && a.tipo !== 'NOTA_ADICIONADA' && a.descricao !== TITULO[a.tipo] ? ` — ${a.descricao}` : TITULO[a.tipo] ? '' : a.descricao}
                    </span>
                    {a.tipo === 'VENDA_REGISTRADA' && a.dados?.valor != null && (
                      <span className="venda-tl"><strong className="num">{moeda(a.dados.valor)}</strong> · venda de {data(a.dados.data)}{a.dados.oportunidadeFechada ? ' · oportunidade fechada como ganha' : ''}</span>
                    )}
                    {(a.tipo === 'NOTA_ADICIONADA' || a.dados?.mensagem) && <span className="bubble">{a.tipo === 'NOTA_ADICIONADA' ? a.descricao : a.dados.mensagem}</span>}
                    {EMAIL_TIPOS.has(a.tipo) && a.dados.assunto && (
                      <>
                        <span className="small" style={{ fontWeight: 500 }}>{a.dados.assunto}{a.dados.anexos?.length ? ` · ${a.dados.anexos.length} anexo${a.dados.anexos.length > 1 ? 's' : ''}` : ''}</span>
                        {a.dados.trecho && <span className={a.tipo === 'EMAIL_RECEBIDO' ? 'citacao' : 'bubble'}>{a.dados.trecho}</span>}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {a.tipo === 'EMAIL_RECEBIDO' && a.dados.emailId && !emailBloqueado && (
                            <button className="btn sm primary" onClick={() => abrirEmail({ id: a.dados.emailId!, assunto: a.dados.assunto! })}><Icon name="responder" size={14} />Responder</button>
                          )}
                          <button className="btn sm" onClick={() => setModal('conversa')}>Ver conversa</button>
                        </div>
                      </>
                    )}
                    <span className="small faint">{a.tipo === 'EMAIL_RECEBIDO' && a.dados.de ? `De ${a.dados.de}` : a.usuario ?? 'Sistema'}{a.campanha ? ` · ${a.campanha}` : ''}</span>
                  </div>
                </li>
              ))}
            </ol>
            {tl.hasNextPage && <button className="btn sm" onClick={() => tl.fetchNextPage()} disabled={tl.isFetchingNextPage}>Carregar mais</button>}
          </section>
        </div>
      </div>

      {modal === 'whatsapp' && (
        <ModalWhatsapp cliente={c} vendedor={me!.usuario.nome} empresa={me!.empresa.nome} onClose={() => setModal(null)}
          onRegistrado={async () => { setModal(null); await recarregar(); toast.show('Contato registrado.'); }} />
      )}
      {modal === 'email' && (
        <ModalEmail cliente={c} respostaA={respostaA} onClose={() => setModal(null)}
          onEnviado={async (comRetorno) => {
            setModal(null);
            await Promise.all([recarregar(), qc.invalidateQueries({ queryKey: ['retornos'] }), qc.invalidateQueries({ queryKey: ['minha-caixa'] })]);
            toast.show(comRetorno ? 'E-mail enviado · retorno agendado.' : 'E-mail enviado.');
          }} />
      )}
      {modal === 'conversa' && (
        <ModalConversa clienteId={c.id} podeResponder={!emailBloqueado} onClose={() => setModal(null)} onResponder={(r) => abrirEmail(r)} />
      )}
      {modal === 'contato' && (
        <ModalContato cliente={c} inicial={negInicial} onClose={() => setModal(null)}
          onRegistrado={async ({ novoStatus, venda, negociacao }) => {
            setModal(null);
            await recarregar();
            if (novoStatus || venda || negociacao) await Promise.all([qc.invalidateQueries({ queryKey: ['clientes'] }), qc.invalidateQueries({ queryKey: ['dashboard'] }), qc.invalidateQueries({ queryKey: ['equipe'] }), qc.invalidateQueries({ queryKey: ['negociacoes'] })]);
            const partes = [venda ? `Venda de ${moeda(venda)} registrada` : negociacao ?? 'Contato registrado', novoStatus ? `status alterado para ${novoStatus}` : null];
            toast.show(`${partes.filter(Boolean).join(' · ')}.`);
          }} />
      )}
      {modal === 'retorno' && (
        <ModalNovoRetorno cliente={c} onClose={() => setModal(null)}
          onCriado={async () => { setModal(null); await Promise.all([recarregar(), qc.invalidateQueries({ queryKey: ['retornos'] })]); toast.show('Retorno agendado.'); }} />
      )}
      {modal === 'optout' && (
        <ModalOptOut clienteId={c.id} onClose={() => setModal(null)} onFeito={async () => { setModal(null); await recarregar(); toast.show('Cliente marcado como NÃO CONTACTAR.'); }} />
      )}
      <Toast msg={toast.msg} onDone={toast.clear} />
    </>
  );
}

/** Dados mínimos para montar a mensagem (ficha do cliente, lista de retornos). */
export interface ClienteWhatsapp {
  id: string; nome: string; empresa?: string | null; cidade?: string | null;
  ultimaCompra?: string | null; valorUltimaCompra?: string | number | null;
}

export function ModalWhatsapp({ cliente, vendedor, empresa, onClose, onRegistrado }: {
  cliente: ClienteWhatsapp; vendedor: string; empresa: string; onClose: () => void; onRegistrado: () => void;
}) {
  const [msg, setMsg] = useState('Olá {{primeiro_nome}}, tudo bem? Aqui é {{vendedor}}, da {{empresa_vendedora}}.');
  const [etapa, setEtapa] = useState<'editar' | 'confirmar'>('editar');
  const [erro, setErro] = useState<string | null>(null);
  const previa = renderizar(msg, montarContexto({ cliente, vendedorNome: vendedor, empresaVendedora: empresa, hoje: hojeISO() })).texto;

  async function abrir() {
    setErro(null);
    try {
      await abrirWhatsapp(async () => (await api<{ url: string }>(`/clientes/${cliente.id}/whatsapp-link`, { body: { mensagem: msg } })).url);
      setEtapa('confirmar');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }
  async function confirmar() {
    try {
      await api(`/clientes/${cliente.id}/contatos`, { body: { canal: 'WHATSAPP', mensagem: previa, resultado: 'ENVIADO' } });
      onRegistrado();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <Modal titulo="WhatsApp" onClose={onClose}>
      {etapa === 'editar' ? (
        <>
          <h2 style={{ fontSize: 18 }}>WhatsApp para {cliente.nome}</h2>
          <Erro erro={erro} />
          <label className="field"><span>Mensagem (aceita variáveis como {'{{primeiro_nome}}'})</span>
            <textarea className="textarea" rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} />
          </label>
          <div className="bubble" style={{ alignSelf: 'flex-end', background: '#173A2E', color: '#E7F6EF', border: 0 }}>{previa}</div>
          <button className="btn primary lg" onClick={abrir}><Icon name="whatsapp" />Abrir WhatsApp</button>
        </>
      ) : (
        <>
          <h2 style={{ fontSize: 20 }}>Enviou para {cliente.nome.split(' ')[0]}?</h2>
          <Erro erro={erro} />
          <button className="btn primary xl" onClick={confirmar}>Sim, enviei</button>
          <button className="btn lg" onClick={onClose}>Não enviei</button>
        </>
      )}
    </Modal>
  );
}

const CANAIS_CONTATO: Array<[string, string]> = [['LIGACAO', 'Ligação'], ['WHATSAPP', 'WhatsApp'], ['PRESENCIAL', 'Visita'], ['EMAIL', 'E-mail']];
const AVISO_GRUPO: Record<string, string> = {
  PROSPECT: 'Continua no grupo Prospect.',
  ATIVO: 'Passa a contar na carteira ativa (indicadores e campanhas).',
  INATIVO: 'Sai da carteira ativa: deixa de contar nos indicadores.',
};

/** "AAAA-MM-DDTHH:mm" no horário local (valor do <input type="datetime-local">). */
const dataHoraLocal = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
/** Validação da data do contato (o servidor valida de novo): até 30 dias para trás, não no futuro. */
function erroDataContato(v: string): string | null {
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return 'Informe a data e a hora do contato.';
  if (d.getTime() > Date.now() + 5 * 60_000) return 'A data do contato não pode ser no futuro.';
  const limite = new Date();
  limite.setHours(0, 0, 0, 0);
  limite.setDate(limite.getDate() - 30);
  if (d < limite) return 'Só dá para registrar contatos dos últimos 30 dias.';
  return null;
}

function ModalContato({ cliente, inicial, onClose, onRegistrado }: {
  cliente: Pick<Cliente, 'id' | 'statusId' | 'statusNome' | 'statusCor' | 'comunicacao' | 'oportunidades'>;
  /** Abre já numa opção de negociação (botões rápidos do card Negociações) */
  inicial?: { opcao: string; oportunidadeId: string } | null;
  onClose: () => void; onRegistrado: (r: { novoStatus: string | null; venda: number | null; negociacao: string | null }) => void;
}) {
  const negCfg = useConfigNegociacao();
  const usaNeg = !!negCfg.data?.ativa;
  const [neg, setNegState] = useState<EstadoNeg>(() => ({
    ...estadoNegInicial(),
    ...(inicial && negCfg.data ? estadoPara(negCfg.data, cliente.oportunidades.filter((o) => o.status === 'ABERTA'), inicial.opcao, inicial.oportunidadeId) : {}),
  }));
  const setNeg = (p: Partial<EstadoNeg>) => setNegState((x) => ({ ...x, ...p }));
  const bloqueado = cliente.comunicacao === 'NAO_CONTACTAR';
  const statusLista = useStatusClientes();
  const [canal, setCanal] = useState('LIGACAO');
  // data e hora do contato (specs/23): vem com agora; só vai para a API se a pessoa mudar
  const [quando, setQuando] = useState(() => dataHoraLocal(new Date()));
  const [quandoEditado, setQuandoEditado] = useState(false);
  const erroQuando = quandoEditado ? erroDataContato(quando) : null;
  const diaQuando = quando.slice(0, 10);
  const [statusId, setStatusId] = useState(cliente.statusId);
  const [valor, setValor] = useState('');
  const [dataVenda, setDataVenda] = useState(hojeISO());
  const [atualizarUltimaCompra, setAtualizarUltimaCompra] = useState(true);
  // specs/27: o vendedor avança/fecha só as dele; as dos colegas aparecem como aviso
  const { me, pode } = useAuth();
  const veTodas = pode('RELATORIOS_TENANT');
  const todasAbertas = cliente.oportunidades.filter((o) => o.status === 'ABERTA');
  const abertas = todasAbertas.filter((o) => !deOutro(o, me?.usuario.id, veTodas));
  const deOutros = todasAbertas.filter((o) => deOutro(o, me?.usuario.id, veTodas));
  const [oportunidadeId, setOportunidadeId] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [tentou, setTentou] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const grupos = porGrupo(statusLista.data);
  const sel = statusLista.data?.find((s) => s.id === statusId);
  const mudou = statusId !== cliente.statusId;
  const valorN = valorDe(valor);
  const vendeu = valorN > 0;
  const hoje = hojeISO();
  const erroData = !vendeu ? null : !dataVenda ? 'Informe a data da venda.' : dataVenda > hoje ? 'A data da venda não pode ser no futuro.' : null;
  // comprou e ainda está num status de Prospect: oferece o status ativo (o padrão para novos, se for do grupo)
  const ativo = statusLista.data?.find((s) => s.ativo && s.grupo === 'ATIVO' && s.padraoNovo)
    ?? grupos.find((g) => g.grupo === 'ATIVO')?.itens[0];
  const vendeuNeg = usaNeg && neg.opcao === 'GANHO' && valorDeTexto(neg.valor) > 0;
  const sugerirAtivo = (usaNeg ? vendeuNeg : vendeu) && sel?.grupo === 'PROSPECT' && !!ativo;
  const opcaoNeg = usaNeg && neg.opcao !== 'NADA'
    ? neg.opcao === 'GANHO' ? negCfg.data!.ganho?.nome ?? 'Venda' : neg.opcao === 'PERDIDO' ? negCfg.data!.perdido?.nome ?? 'Perda'
      : negCfg.data!.etapas.find((e) => e.id === neg.opcao)?.nome ?? null
    : null;

  async function registrar() {
    setTentou(true);
    if (usaNeg ? erroNeg(negCfg.data!, neg) : erroData) return;
    if (erroQuando) return;
    setErro(null);
    setSalvando(true);
    try {
      await api(`/clientes/${cliente.id}/contatos`, {
        body: {
          canal, observacao: obs.trim() || undefined, ...(mudou ? { statusId } : {}),
          ...(quandoEditado ? { realizadoEm: new Date(quando).toISOString() } : {}),
          ...(usaNeg
            ? (neg.opcao !== 'NADA' ? { negociacao: payloadNeg(negCfg.data!, neg) } : {})
            : vendeu ? { venda: { valor: valorN, data: dataVenda, atualizarUltimaCompra, ...(oportunidadeId ? { oportunidadeId } : {}) } } : {}),
        },
      });
      onRegistrado({
        novoStatus: mudou ? sel?.nome ?? null : null,
        venda: usaNeg ? (vendeuNeg ? valorDeTexto(neg.valor) : null) : vendeu ? valorN : null,
        negociacao: usaNeg && opcaoNeg && neg.opcao !== 'GANHO' ? `${opcaoNeg} registrado` : null,
      });
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Registrar contato" onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>Registrar contato</h2>
      <Erro erro={erro} />
      <div className="field"><span>Canal</span>
        <div className="opts" role="group" aria-label="Canal do contato">
          {CANAIS_CONTATO.map(([v, l]) => (
            <button key={v} type="button" className={`opt${canal === v ? ' on' : ''}`} aria-pressed={canal === v}
              disabled={v === 'WHATSAPP' && bloqueado} onClick={() => setCanal(v)}>
              {v === 'WHATSAPP' && <Icon name="whatsapp" size={14} />}{l}
            </button>
          ))}
        </div>
        {bloqueado && <span className="hint warn">WhatsApp indisponível: cliente marcado como NÃO CONTACTAR.</span>}
      </div>

      <div className="field">
        <label htmlFor="quando-contato">{canal === 'PRESENCIAL' ? 'Data e hora da visita' : 'Data e hora do contato'}</label>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input id="quando-contato" className="input num" type="datetime-local" style={{ maxWidth: 240, ...(erroQuando ? { borderColor: 'var(--red-border)' } : {}) }}
            value={quando} max={dataHoraLocal(new Date())}
            onChange={(e) => { setQuando(e.target.value); setQuandoEditado(true); }} />
          {quandoEditado && <button type="button" className="btn sm" onClick={() => { setQuando(dataHoraLocal(new Date())); setQuandoEditado(false); }}>Usar agora</button>}
        </div>
        {erroQuando ? <span className="hint err">{erroQuando}</span>
          : quandoEditado && diaQuando !== hoje
            ? <span className="hint">Vai contar em {data(diaQuando)}: na timeline do cliente, em Contatos do dia e na Atividade da equipe.</span>
            : <span className="hint">Vem com a data e a hora de agora. Mude se o contato foi antes.</span>}
      </div>

      <div className="field"><span id="l-status-contato">Status do cliente</span>
        <div className="status-chips" role="group" aria-labelledby="l-status-contato">
          {grupos.filter((g) => g.itens.length > 0).map((g) => (
            <div key={g.grupo} className="status-chips-linha">
              <span className="status-chips-grupo">{g.nome}</span>
              <div className="status-chips-lista">
                {g.itens.map((s) => (
                  <button key={s.id} type="button" className="status-chip" aria-pressed={statusId === s.id} onClick={() => setStatusId(s.id)}>
                    <span className="status-dot" style={{ background: CORES_UI[s.cor].fg }} aria-hidden="true" />{s.nome}
                    {s.id === cliente.statusId && <span className="status-chip-atual">atual</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {mudou && sel && <span className="hint">{cliente.statusNome} → {sel.nome}. {AVISO_GRUPO[sel.grupo]}</span>}
      </div>

      {usaNeg ? (
        <>
          <SecaoNegociacao cfg={negCfg.data!} abertas={abertas} s={neg} set={setNeg} tentou={tentou} deOutros={deOutros} />
          {sugerirAtivo && (
            <div className="venda-sugestao">
              <span>Cliente comprou e ainda está como {sel!.nome}.</span>
              <button type="button" className="btn sm" onClick={() => setStatusId(ativo!.id)}>Passar para {ativo!.nome}</button>
            </div>
          )}
        </>
      ) : (
      <div className={`venda-box${vendeu ? ' on' : ''}`}>
        <div className="venda-campos">
          <label className="field"><span>Valor vendido <span className="faint">(se vendeu)</span></span>
            <span className="input-prefixo"><span aria-hidden="true">R$</span>
              <input className="input num" inputMode="decimal" placeholder="0,00" value={valor} aria-label="Valor vendido em reais"
                onChange={(e) => setValor(e.target.value.replace(/[^\d.,]/g, ''))} />
            </span>
          </label>
          <label className="field"><span>Data da venda{vendeu ? ' *' : ''}</span>
            <input className={`input num${tentou && erroData ? ' invalido' : ''}`} type="date" max={hoje} value={dataVenda}
              onChange={(e) => { setDataVenda(e.target.value); setTentou(false); }} />
          </label>
        </div>
        {tentou && erroData && <span className="hint err">{erroData}</span>}
        {vendeu && (
          <>
            <span className="small faint">A venda conta no Dashboard, na meta e nas vendas do mês na data da venda, não no dia do registro.</span>
            {abertas.length > 0 && (
              <label className="field"><span>Oportunidade</span>
                <select className="select" value={oportunidadeId} onChange={(e) => setOportunidadeId(e.target.value)}>
                  <option value="">Nova venda</option>
                  {abertas.map((o) => <option key={o.id} value={o.id}>Fechar “{o.titulo}” como ganha ({o.etapa})</option>)}
                </select>
              </label>
            )}
            <label className="check" style={{ alignItems: 'flex-start' }}>
              <input type="checkbox" checked={atualizarUltimaCompra} onChange={(e) => setAtualizarUltimaCompra(e.target.checked)} />
              <span>Atualizar a última compra do cliente<br /><span className="small faint">Usada no filtro “sem comprar há X dias” das campanhas.</span></span>
            </label>
            {sugerirAtivo && (
              <div className="venda-sugestao">
                <span>Cliente comprou e ainda está como {sel!.nome}.</span>
                <button type="button" className="btn sm" onClick={() => setStatusId(ativo!.id)}>Passar para {ativo!.nome}</button>
              </div>
            )}
          </>
        )}
      </div>
      )}

      <label className="field"><span>Observação</span>
        <textarea className="textarea" rows={2} maxLength={1000} value={obs} onChange={(e) => setObs(e.target.value)}
          placeholder={vendeu || (usaNeg && neg.opcao !== 'NADA') ? 'Ex.: pedido nº 4512, prazo 30/60' : 'Ex.: pediu tabela de preços nova'} />
      </label>
      <button className={`btn lg ${(usaNeg ? neg.opcao === 'GANHO' : vendeu) ? 'venda' : 'primary'}`} disabled={salvando} onClick={registrar}>
        {salvando ? 'Registrando…'
          : usaNeg ? (neg.opcao === 'GANHO' ? `Registrar venda de ${moeda(valorDeTexto(neg.valor))}` : opcaoNeg ? `Registrar ${opcaoNeg.toLowerCase()}` : mudou && sel ? `Registrar e mudar para ${sel.nome}` : 'Registrar contato')
          : vendeu ? `Registrar venda de ${moeda(valorN)}` : mudou && sel ? `Registrar e mudar para ${sel.nome}` : 'Registrar contato'}
      </button>
    </Modal>
  );
}

/** "3.480,50" | "3480,5" | "3480.50" → 3480.5 */
function valorDe(v: string): number {
  const s = v.trim();
  if (!s) return 0;
  const norm = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  const n = Number(norm);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
}

function ModalOptOut({ clienteId, onClose, onFeito }: { clienteId: string; onClose: () => void; onFeito: () => void }) {
  const [motivo, setMotivo] = useState('PEDIU_NAO_RECEBER');
  const [erro, setErro] = useState<string | null>(null);
  return (
    <Modal titulo="Não contactar" onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>Marcar como NÃO CONTACTAR</h2>
      <p className="muted" style={{ margin: 0 }}>O cliente sai de todas as campanhas e o botão de WhatsApp fica bloqueado. Só um administrador pode reverter.</p>
      <Erro erro={erro} />
      <label className="field"><span>Motivo</span>
        <select className="select" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          <option value="PEDIU_NAO_RECEBER">Pediu para não receber mensagens</option>
          <option value="NUMERO_ERRADO">Número errado</option>
          <option value="OUTRO">Outro</option>
        </select>
      </label>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn danger" onClick={async () => {
          try { await api(`/clientes/${clienteId}/nao-contactar`, { body: { motivo } }); onFeito(); }
          catch (e) { setErro(mensagemErro(e)); }
        }}>Confirmar</button>
      </div>
    </Modal>
  );
}

const TIPOS_RETORNO: Array<[string, string]> = [['WHATSAPP', 'WhatsApp'], ['LIGACAO', 'Ligação'], ['VISITA', 'Visita'], ['REUNIAO', 'Reunião'], ['EMAIL', 'E-mail'], ['OUTRO', 'Outro']];

function amanhaISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function ModalNovoRetorno({ cliente, onClose, onCriado }: { cliente: Cliente; onClose: () => void; onCriado: () => void }) {
  const { me, pode } = useAuth();
  const admin = pode('RELATORIOS_TENANT');
  const usuarios = useUsuarios(admin);
  const bloqueado = cliente.comunicacao === 'NAO_CONTACTAR';
  const [f, setF] = useState({
    tipo: bloqueado ? 'LIGACAO' : 'WHATSAPP',
    titulo: `Retornar contato com ${cliente.nome.split(/\s+/)[0]}`,
    descricao: '',
    data: amanhaISO(),
    hora: '09:00',
    responsavelId: cliente.vendedorId ?? me!.usuario.id,
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      await api('/retornos', {
        body: {
          clienteId: cliente.id, tipo: f.tipo, titulo: f.titulo, data: f.data, hora: f.hora,
          ...(f.descricao.trim() ? { descricao: f.descricao.trim() } : {}),
          ...(admin ? { responsavelId: f.responsavelId } : {}),
        },
      });
      onCriado();
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Agendar retorno" onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>Agendar retorno · {cliente.nome}</h2>
      <Erro erro={erro} />
      <div className="field"><span>Como vai retornar</span>
        <div className="opts">
          {TIPOS_RETORNO.map(([v, l]) => (
            <button key={v} type="button" className={`opt${f.tipo === v ? ' on' : ''}`} aria-pressed={f.tipo === v}
              disabled={v === 'WHATSAPP' && bloqueado} onClick={() => setF({ ...f, tipo: v })}>{l}</button>
          ))}
        </div>
      </div>
      <label className="field"><span>Título</span><input className="input" value={f.titulo} maxLength={150} onChange={(e) => setF({ ...f, titulo: e.target.value })} /></label>
      <div style={{ display: 'flex', gap: 10 }}>
        <label className="field" style={{ flex: 1 }}><span>Data</span><input className="input" type="date" min={hojeISO()} value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} /></label>
        <label className="field" style={{ width: 120 }}><span>Hora</span><input className="input" type="time" value={f.hora} onChange={(e) => setF({ ...f, hora: e.target.value })} /></label>
      </div>
      <div className="opts">
        {[[0, 'Hoje'], [1, 'Amanhã'], [3, 'Em 3 dias'], [7, 'Em 1 semana']].map(([dias, l]) => {
          const d = new Date(); d.setDate(d.getDate() + (dias as number));
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return <button key={dias} type="button" className={`opt${f.data === iso ? ' on' : ''}`} onClick={() => setF({ ...f, data: iso })}>{l}</button>;
        })}
      </div>
      <label className="field"><span>Observação (opcional)</span><textarea className="textarea" rows={2} maxLength={500} value={f.descricao} onChange={(e) => setF({ ...f, descricao: e.target.value })} placeholder="Ex.: pediu para ligar depois do fechamento do mês" /></label>
      {admin && (
        <label className="field"><span>Responsável</span>
          <select className="select" value={f.responsavelId} onChange={(e) => setF({ ...f, responsavelId: e.target.value })}>
            {usuarios.data?.filter((u) => u.status === 'ATIVO').map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </label>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={salvando || f.titulo.trim().length < 2 || !f.data} onClick={salvar}>{salvando ? 'Salvando…' : 'Agendar'}</button>
      </div>
    </Modal>
  );
}
